// Klaviyo-szegmensek (3.2): every account's engagement segments — "engaged
// (60)", "Engaged 60" and the like — grouped by country, as the daily refresh
// left them: how many members Klaviyo has, how many of them are contacts here,
// how many of those may be mailed, and when that was counted. Read-only: the
// rules live in Klaviyo. A row opens the segment; the campaign wizard offers
// the ready ones like any other segment.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchKlaviyoSegments } from "@/lib/api";
import type { KlaviyoSegment } from "@/lib/api";
import { DataTable } from "@/components/emc/DataTable";
import { Country } from "@/components/emc/Flag";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { cn } from "@/lib/utils";
import { COUNTRIES, messageOf } from "@/pages/segments/data";
import { ago } from "@/pages/segments/format";
import { groupByCountry, rowWords } from "@/pages/segments/klaviyo";

/** "Klaviyo" beside a name: where the segment comes from, not a state. */
export function KlaviyoMark({ className }: { className?: string }) {
  return (
    <span
      title="A Klaviyóból átvett szegmens: a tagságát naponta a Klaviyo adja, itt nem szerkeszthető."
      className={cn(
        "data-mono inline-flex h-5 shrink-0 items-center rounded-pill border border-line px-1.5 text-label text-muted-foreground",
        className,
      )}
    >
      Klaviyo
    </span>
  );
}

export function KlaviyoSegments({ needle, onCount }: { needle: string; onCount?: (total: number) => void }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<KlaviyoSegment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  async function load() {
    try {
      const answer = await fetchKlaviyoSegments();
      setRows(answer);
      setError(null);
      setNow(Date.now());
      onCount?.(answer.length);
    } catch (raised: unknown) {
      setError(messageOf(raised));
      setRows((current) => current ?? []);
    }
  }

  useEffect(() => {
    void load();
    // Once: the refresh is daily, a page open for minutes is not behind.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = useMemo(() => groupByCountry(rows ?? [], COUNTRIES, needle), [rows, needle]);

  const columns: ColumnDef<KlaviyoSegment>[] = [
    {
      id: "name",
      header: "Név",
      cell: ({ row }) => (
        <span className="flex max-w-120 min-w-0 flex-col">
          <span className="flex items-center gap-2">
            <Link
              to={`/szegmensek/${row.original.segment_id}`}
              className="font-medium text-ink hover:text-blue-ink"
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.name}
            </Link>
            <KlaviyoMark />
          </span>
          {row.original.name !== row.original.klaviyo_name && (
            <span className="truncate text-label text-muted-foreground">
              a Klaviyóban: {row.original.klaviyo_name}
            </span>
          )}
        </span>
      ),
      meta: { wrap: true },
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) => {
        const words = rowWords(row.original);
        return (
          <StatusPill tone={words.tone} title={words.standing}>
            {words.pill}
          </StatusPill>
        );
      },
    },
    {
      id: "klaviyo",
      header: "Tag a Klaviyóban",
      cell: ({ row }) => rowWords(row.original).inKlaviyo,
      meta: { align: "right" },
    },
    {
      id: "found",
      header: "Ebből nálunk",
      cell: ({ row }) => {
        const words = rowWords(row.original);
        return (
          <span className="flex flex-col items-end">
            <span>{words.found}</span>
            {words.notFound && <span className="text-label text-muted-foreground">{words.notFound}</span>}
          </span>
        );
      },
      meta: { align: "right" },
    },
    {
      id: "sendable",
      header: "Ebből küldhető",
      cell: ({ row }) => (
        <span className="font-semibold" title="Feliratkozott, és nincs rajta élő kizárás (globális vagy az országé).">
          {rowWords(row.original).sendable}
        </span>
      ),
      meta: { align: "right" },
    },
    {
      id: "refreshed",
      header: "Frissítve",
      cell: ({ row }) => (
        <span className="flex flex-col items-end" title={minute(row.original.refreshed_at)}>
          <span>{row.original.refreshed_at ? ago(row.original.refreshed_at, now) : "még nem"}</span>
          {row.original.gone_at && (
            <span className="text-label text-muted-foreground">megszűnt {ago(row.original.gone_at, now)}</span>
          )}
        </span>
      ),
      meta: { align: "right" },
    },
  ];

  if (rows === null && error === null) return <LoadingState lines={4} label="A Klaviyo-szegmensek betöltése…" />;

  return (
    <div className="flex flex-col gap-6">
      <Callout>
        A Klaviyo engagement-szegmensei (a nevükben „engaged” van, „never” nincs), országonként. A tagságukat
        naponta egyszer a Klaviyo adja: a Klaviyo-profil azonosítója alapján párosítjuk a mi kontaktjainkkal. Itt
        nem szerkeszthetők; kampány közönségének vagy kizárásának választhatók. A kampány a küldéskor a
        leiratkozottakat és a kizárt címeket ezekből is kiveszi.
      </Callout>
      {error !== null && <ErrorState message={error} onRetry={() => void load()} />}
      {groups.length === 0 ? (
        <EmptyState
          title={needle ? `Nincs „${needle}” nevű Klaviyo-szegmens.` : "Még nincs átvett Klaviyo-szegmens."}
          description={
            needle
              ? undefined
              : "A Klaviyo-szinkron naponta egyszer veszi át őket; az első futás után itt jelennek meg."
          }
        />
      ) : (
        groups.map((group) => (
          <section key={group.country} className="flex flex-col gap-2" aria-label={`${group.country} Klaviyo-szegmensei`}>
            <h2 className="flex items-center gap-3">
              <Country code={group.country} />
              <span className="text-label font-normal text-muted-foreground">{count(group.rows.length)} szegmens</span>
            </h2>
            <DataTable
              label={`${group.country} Klaviyo-szegmensei`}
              columns={columns}
              data={group.rows}
              getRowId={(row) => String(row.segment_id)}
              onRowClick={(row) => navigate(`/szegmensek/${row.segment_id}`)}
            />
          </section>
        ))
      )}
    </div>
  );
}
